// Design tokens: shared styles and variables (with modes). Bound properties are resolved
// into concrete node props by a derived pass, so renderers/exports need no token logic.
import type { Document } from './document.ts';
import type { Color, NodeData, NodeId, NodeProps, Op, Paint, VariableScope, VariableValue } from './types.ts';

export const TEXT_STYLE_KEYS = ['fontFamily', 'fontSize', 'fontWeight', 'italic', 'lineHeight', 'letterSpacing', 'textCase', 'textDecoration'] as const;

/** Properties a variable can be bound to, with the variable type they accept. */
export const BINDABLE: Record<string, 'COLOR' | 'FLOAT' | 'STRING' | 'BOOLEAN'> = {
  fill: 'COLOR',
  stroke: 'COLOR',
  textColor: 'COLOR',
  itemSpacing: 'FLOAT',
  counterAxisSpacing: 'FLOAT',
  paddingTop: 'FLOAT',
  paddingRight: 'FLOAT',
  paddingBottom: 'FLOAT',
  paddingLeft: 'FLOAT',
  cornerRadius: 'FLOAT',
  width: 'FLOAT',
  height: 'FLOAT',
  opacity: 'FLOAT',
  fontSize: 'FLOAT',
  lineHeight: 'FLOAT',
  letterSpacing: 'FLOAT',
  strokeWeight: 'FLOAT',
  characters: 'STRING',
  visible: 'BOOLEAN',
};

/** The scopes that admit binding `prop` on node `n` (any one of them is enough). */
export function scopesFor(n: Pick<NodeData, 'type'>, prop: string): VariableScope[] {
  const textual = n.type === 'TEXT';
  const frameLike = n.type === 'FRAME' || n.type === 'COMPONENT' || n.type === 'INSTANCE' || n.type === 'SECTION';
  switch (prop) {
    case 'fill':
      return ['ALL_FILLS', textual ? 'TEXT_FILL' : frameLike ? 'FRAME_FILL' : 'SHAPE_FILL'];
    case 'textColor':
      return ['ALL_FILLS', 'TEXT_FILL'];
    case 'stroke':
      return ['STROKE_COLOR'];
    case 'cornerRadius':
      return ['CORNER_RADIUS'];
    case 'width':
    case 'height':
      return ['WIDTH_HEIGHT'];
    case 'itemSpacing':
    case 'counterAxisSpacing':
    case 'paddingTop':
    case 'paddingRight':
    case 'paddingBottom':
    case 'paddingLeft':
      return ['GAP'];
    case 'opacity':
      return ['OPACITY'];
    case 'fontSize':
      return ['FONT_SIZE'];
    case 'lineHeight':
      return ['LINE_HEIGHT'];
    case 'letterSpacing':
      return ['LETTER_SPACING'];
    case 'strokeWeight':
      return ['STROKE_FLOAT'];
    case 'characters':
      return ['TEXT_CONTENT'];
    default:
      return []; // e.g. visibility: booleans have no scopes
  }
}

/** Why `v` can't be bound to `prop` on `n`, or null when it can. */
export function bindProblem(v: NodeData, n: Pick<NodeData, 'type'>, prop: string): string | null {
  const want = BINDABLE[prop];
  if (!want) return `Property "${prop}" can't be bound`;
  // text content also shows numbers (a counter's value)
  if (v.resolvedType !== want && !(prop === 'characters' && v.resolvedType === 'FLOAT')) return `${v.name} is a ${v.resolvedType?.toLowerCase()} variable; ${prop} needs a ${want.toLowerCase()}`;
  const scopes = v.scopes;
  if (!scopes || !scopes.length || scopes.includes('ALL_SCOPES')) return null;
  const allowed = scopesFor(n, prop);
  if (!allowed.length || allowed.some((s) => scopes.includes(s))) return null;
  return `${v.name} is scoped to ${scopes.map((s) => s.toLowerCase().replace(/_/g, ' ')).join(', ')}, so it can't be used for ${prop}`;
}

/** Variables that may be bound to `prop` on `n` (type and scope). */
export function bindableVariables(doc: Document, n: Pick<NodeData, 'type'>, prop: string): NodeData[] {
  return variables(doc).filter((v) => !bindProblem(v, n, prop));
}

/** The scopes that make sense for a variable type (for scope pickers). */
export const SCOPES_BY_TYPE: Record<'COLOR' | 'FLOAT' | 'STRING' | 'BOOLEAN', VariableScope[]> = {
  COLOR: ['ALL_FILLS', 'FRAME_FILL', 'SHAPE_FILL', 'TEXT_FILL', 'STROKE_COLOR', 'EFFECT_COLOR'],
  FLOAT: ['CORNER_RADIUS', 'WIDTH_HEIGHT', 'GAP', 'OPACITY', 'STROKE_FLOAT', 'EFFECT_FLOAT', 'FONT_SIZE', 'LINE_HEIGHT', 'LETTER_SPACING'],
  STRING: ['TEXT_CONTENT'],
  BOOLEAN: [],
};

/** A variable's CSS custom property: its WEB code syntax, else derived from its name ("Color/Primary" → --color-primary). */
export function cssVariableName(v: Pick<NodeData, 'name' | 'codeSyntax'>): string {
  const web = v.codeSyntax?.WEB?.trim();
  if (web) {
    const m = web.match(/^var\(\s*(--[\w-]+)/);
    if (m) return m[1];
    return web.startsWith('--') ? web : `--${web}`;
  }
  return `--${v.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'token'}`;
}

export function styles(doc: Document, type?: NodeData['styleType']): NodeData[] {
  return doc.children(doc.rootId).filter((n) => n.type === 'STYLE' && (!type || n.styleType === type));
}

export function collections(doc: Document): NodeData[] {
  return doc.children(doc.rootId).filter((n) => n.type === 'VARIABLE_COLLECTION');
}

export function variables(doc: Document, collectionId?: NodeId): NodeData[] {
  const cols = collectionId ? [doc.must(collectionId)] : collections(doc);
  return cols.flatMap((c) => doc.children(c.id));
}

/** Find a style by id or name (case-insensitive, "/" groups allowed). */
export function findStyle(doc: Document, ref: string, type?: NodeData['styleType']): NodeData | undefined {
  const byId = doc.get(ref);
  if (byId?.type === 'STYLE') return byId;
  const k = ref.toLowerCase();
  return styles(doc, type).find((s) => s.name.toLowerCase() === k) ?? styles(doc).find((s) => s.name.toLowerCase() === k);
}

/** Find a variable by id, "Collection/name" or plain name. */
export function findVariable(doc: Document, ref: string): NodeData | undefined {
  const byId = doc.get(ref);
  if (byId?.type === 'VARIABLE') return byId;
  const k = ref.toLowerCase();
  for (const c of collections(doc)) {
    for (const v of doc.children(c.id)) {
      if (v.name.toLowerCase() === k || `${c.name}/${v.name}`.toLowerCase() === k) return v;
    }
  }
  return undefined;
}

export function findCollection(doc: Document, ref: string): NodeData | undefined {
  const byId = doc.get(ref);
  if (byId?.type === 'VARIABLE_COLLECTION') return byId;
  return collections(doc).find((c) => c.name.toLowerCase() === ref.toLowerCase());
}

/** The mode a node uses for a collection (nearest ancestor override, else the collection default). */
export function modeFor(doc: Document, nodeId: NodeId | null, collectionId: NodeId): string | undefined {
  let n = nodeId ? doc.get(nodeId) : undefined;
  while (n) {
    const m = n.variableModes?.[collectionId];
    if (m) return m;
    n = doc.get(n.parent);
  }
  const c = doc.get(collectionId);
  return c?.defaultModeId ?? c?.modes?.[0]?.id;
}

/** The extension chain from `collId` down to `baseId` (inclusive), or null if it doesn't extend it. */
function extensionChain(doc: Document, collId: NodeId, baseId: NodeId): NodeData[] | null {
  const chain: NodeData[] = [];
  let c = doc.get(collId);
  for (let i = 0; c && i < 8; i++) {
    chain.push(c);
    if (c.id === baseId) return chain;
    c = c.extendsCollection ? doc.get(c.extendsCollection) : undefined;
  }
  return null;
}

/** Collections that extend `baseId` (directly or through other extensions). */
export function extensionsOf(doc: Document, baseId: NodeId): NodeData[] {
  return collections(doc).filter((c) => c.id !== baseId && extensionChain(doc, c.id, baseId));
}

/**
 * The mode a node uses for a collection, and the extended collections in effect: the nearest
 * frame that picked a mode of the collection or of a collection extending it decides.
 */
function effectiveMode(doc: Document, nodeId: NodeId | null, baseId: NodeId): { mode?: string; chain: NodeData[] } {
  let n = nodeId ? doc.get(nodeId) : undefined;
  while (n) {
    // on one frame, the most specific collection (the longest extension chain) wins
    let best: { mode: string; chain: NodeData[] } | null = null;
    for (const [cid, mode] of Object.entries(n.variableModes ?? {})) {
      const chain = extensionChain(doc, cid, baseId);
      if (chain && (!best || chain.length > best.chain.length)) best = { mode, chain };
    }
    if (best) return best;
    n = doc.get(n.parent);
  }
  const c = doc.get(baseId);
  return { mode: c?.defaultModeId ?? c?.modes?.[0]?.id, chain: c ? [c] : [] };
}

export function resolveVariable(doc: Document, varId: NodeId, nodeId: NodeId | null, depth = 0): VariableValue | undefined {
  const v = doc.get(varId);
  if (!v || v.type !== 'VARIABLE' || depth > 8) return undefined;
  const coll = doc.get(v.parent);
  const { mode, chain } = coll ? effectiveMode(doc, nodeId, coll.id) : { mode: undefined, chain: [] };
  const values = v.valuesByMode ?? {};
  // an extended collection's override wins over the values it inherits
  const override = mode ? chain.find((c) => c.variableOverrides?.[varId]?.[mode] !== undefined)?.variableOverrides![varId][mode] : undefined;
  let val: VariableValue | undefined = override ?? (mode ? values[mode] : undefined) ?? (coll?.defaultModeId ? values[coll.defaultModeId] : undefined) ?? Object.values(values)[0];
  if (val && typeof val === 'object' && 'alias' in val) val = resolveVariable(doc, val.alias, nodeId, depth + 1);
  return val;
}

function withColor(paints: Paint[], c: Color): Paint[] {
  const first = paints[0];
  if (first?.type === 'SOLID') return [{ ...first, color: c }, ...paints.slice(1)];
  return [{ type: 'SOLID', color: c }, ...paints.slice(1)];
}

/** Concrete props a node should have given its styles and variable bindings. */
export function tokenProps(doc: Document, n: NodeData): NodeProps {
  const p: NodeProps = {};
  const s = (id?: NodeId) => (id ? doc.get(id) : undefined);
  const fill = s(n.fillStyleId);
  if (fill?.type === 'STYLE') p.fills = structuredClone(fill.fills);
  const stroke = s(n.strokeStyleId);
  if (stroke?.type === 'STYLE') p.strokes = structuredClone(stroke.fills);
  const effect = s(n.effectStyleId);
  if (effect?.type === 'STYLE') p.effects = structuredClone(effect.effects);
  const grid = s(n.gridStyleId);
  if (grid?.type === 'STYLE') p.layoutGrids = structuredClone(grid.layoutGrids ?? []);
  const text = s(n.textStyleId);
  if (text?.type === 'STYLE') for (const k of TEXT_STYLE_KEYS) (p as Record<string, unknown>)[k] = text[k];
  for (const [key, varId] of Object.entries(n.boundVariables ?? {})) {
    const v = resolveVariable(doc, varId, n.id);
    if (v === undefined) continue;
    if (key === 'fill' && typeof v === 'object') p.fills = withColor(p.fills ?? n.fills, v as Color);
    else if (key === 'stroke' && typeof v === 'object') p.strokes = withColor(p.strokes ?? n.strokes, v as Color);
    else if (key === 'textColor' && typeof v === 'object') {
      if (n.type === 'TEXT') p.fills = withColor(p.fills ?? n.fills, v as Color);
      else p.textColor = v as Color;
    } else if (key === 'characters' && (typeof v === 'string' || typeof v === 'number')) p.characters = typeof v === 'number' ? String(Math.round(v * 1000) / 1000) : v;
    else if (key === 'visible' && typeof v === 'boolean') p.visible = v;
    else if (typeof v === 'number') (p as Record<string, unknown>)[key] = v;
  }
  return p;
}

function hasTokens(n: NodeData) {
  return !!(n.fillStyleId || n.strokeStyleId || n.textStyleId || n.effectStyleId || n.gridStyleId || (n.boundVariables && Object.keys(n.boundVariables).length));
}

/** Ops that bring token-bound nodes up to date after a change. */
export function resolveTokens(doc: Document, changed: Iterable<NodeId>): Op[] {
  const ids = [...changed];
  const tokenChanged = ids.some((id) => {
    const t = doc.get(id)?.type;
    return t === 'STYLE' || t === 'VARIABLE' || t === 'VARIABLE_COLLECTION';
  });
  const candidates = new Set<NodeId>();
  if (tokenChanged) {
    for (const n of doc.nodes.values()) if (hasTokens(n)) candidates.add(n.id);
  } else {
    for (const id of ids) {
      const n = doc.get(id);
      if (!n) continue;
      if (hasTokens(n)) candidates.add(id);
      if (n.variableModes) doc.walk(id, (d) => void (hasTokens(d) && candidates.add(d.id)));
    }
  }
  const ops: Op[] = [];
  for (const id of candidates) {
    const n = doc.must(id);
    const want = tokenProps(doc, n) as Record<string, unknown>;
    const diff: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(want)) if (JSON.stringify((n as unknown as Record<string, unknown>)[k]) !== JSON.stringify(v)) diff[k] = v;
    if (Object.keys(diff).length) ops.push({ op: 'set', id, props: diff as NodeProps });
  }
  return ops;
}

/** Keys that, when edited directly, detach a node from its style / variable. */
export function detachFor(n: NodeData, props: NodeProps): NodeProps {
  const out: NodeProps = {};
  const has = (k: string) => k in props;
  if (n.fillStyleId && has('fills') && !has('fillStyleId')) out.fillStyleId = undefined;
  if (n.strokeStyleId && has('strokes') && !has('strokeStyleId')) out.strokeStyleId = undefined;
  if (n.effectStyleId && has('effects') && !has('effectStyleId')) out.effectStyleId = undefined;
  if (n.gridStyleId && has('layoutGrids') && !has('gridStyleId')) out.gridStyleId = undefined;
  if (n.textStyleId && !has('textStyleId') && TEXT_STYLE_KEYS.some((k) => has(k))) out.textStyleId = undefined;
  if (n.boundVariables && !has('boundVariables')) {
    const keep = { ...n.boundVariables };
    let changed = false;
    for (const key of Object.keys(keep)) {
      const prop = key === 'fill' || (key === 'textColor' && n.type === 'TEXT') ? 'fills' : key === 'stroke' ? 'strokes' : key;
      if (has(prop)) {
        delete keep[key];
        changed = true;
      }
    }
    if (changed) out.boundVariables = Object.keys(keep).length ? keep : undefined;
  }
  return out;
}

export function variableValueToJson(v: VariableValue | undefined): unknown {
  if (v && typeof v === 'object' && 'r' in v) {
    const h = (x: number) => Math.round(x * 255).toString(16).padStart(2, '0');
    return `#${h(v.r)}${h(v.g)}${h(v.b)}${v.a < 1 ? h(v.a) : ''}`;
  }
  return v;
}
